import { Observable, throwError as observableThrowError } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AccountStatementCreationStatus } from '@models/documents/account-statement.class';
import {
  LegalDocument,
  UserLegalDocument,
} from '@models/documents/legal-documents.class';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { AppConfigurationApis, AppState } from 'src/app';
import { NotificationService } from 'src/core/services/notification.service';
import { ApiGatewayService } from 'src/shared';
@Injectable()
export class DocumentsService {
  private partyId$ = new Observable<string>();
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;
  private gbmComAPI: string;

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private apiGatewayService: ApiGatewayService,
    private http: HttpClient,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
        take(1),
      )
      .subscribe(({ api, gbmComApi }: AppConfigurationApis) => {
        this.gbmAPI = api;
        this.gbmComAPI = gbmComApi;
      });

    this.partyId$ = this.store
      .select('partyId')
      .pipe(filter(Boolean), take(1)) as Observable<string>;
  }

  downloadInvoice(downloadOptions: {
    invoiceId: string;
    fileFormat: number;
    fiscalYear: number;
  }): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/FileManagement/DownloadElectronicInvoice`,
        downloadOptions,
        false,
        {},
        true,
      )
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  public getAccountStatementCreationStatus(): Observable<AccountStatementCreationStatus> {
    return this.http.get<AccountStatementCreationStatus>(
      `${this.gbmAPI}api/FileManagement/GetAccountStatementCreationStatus`,
    );
  }

  getConstancy(downloadOptions: {
    contractId: string;
    processDate: any;
  }): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/Portfolio/GetTaxWithholdingCertificate`,
        downloadOptions,
      )
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getType(downloadOptions: { request: number }): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/Portfolio/GetDescriptionWithholdingType`,
        downloadOptions,
      )
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getStockMarket(downloadOptions: {
    contractId: string;
    processDate: any;
  }): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/Portfolio/GetStockMarketCertificate`,
        downloadOptions,
      )
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  public getUserLegalDocuments$(): Observable<UserLegalDocument[]> {
    return this.partyId$.pipe(
      switchMap((partyId) =>
        this.apiGatewayService.get(
          `${this.gbmComAPI}v2/parties/${partyId}/legal-documents`,
        ),
      ),
    );
  }

  public getLegalDocuments$(): Observable<LegalDocument[]> {
    return this.apiGatewayService.get(`${this.gbmComAPI}v2/legal-documents`);
  }

  public acceptLegalDocument$({
    type,
    version,
  }: LegalDocument): Observable<UserLegalDocument> {
    return this.partyId$.pipe(
      switchMap(
        (partyId) =>
          this.apiGatewayService.post(
            `${this.gbmComAPI}v2/parties/${partyId}/legal-documents`,
            { type, version },
          ) as any,
      ),
    ) as Observable<UserLegalDocument>;
  }
}
